import Status from "../ValueObject/Status";

export default class DocumentDownload {
    public constructor(
        private readonly id: number,
        private readonly resourceId: number,
        private readonly filename: string,
        private readonly filesize: number,
        private readonly url: string,
        private readonly uuid: string,
        private readonly name: string,
        private readonly documentName: string,
        private readonly description: string,
        private readonly mimetype: string,
        private readonly fileId: number,
        private readonly mediaUuid: string,
        private readonly sessionToken: string,
        private readonly playlistId: string | null,
        private readonly entityChanged: any,
        private readonly progress: number,
        private readonly format: string,
        private readonly entityBundle: string,
        private readonly timelapse: number | null,
        private status: Status,
        private position: number,
        private token: string | null = null,
        private partialPosition: number | null = null
    ) {}

    public static from(document: any): DocumentDownload {
        return new DocumentDownload(
            document.id,
            document.resourceId,
            document.filename,
            document.filesize,
            document.url,
            document.uuid,
            document.name,
            document.documentName,
            document.description,
            document.mimetype,
            document.fileId,
            document.mediaUuid,
            document.sessionToken,
            document.playlistId,
            document.entityChanged,
            document.progress,
            document.format,
            document.entityBundle,
            document.timelapse,
            document.status,
            document.position,
            document.token,
            document.partialPosition
        );
    }

    public getId(): number {
        return this.id;
    }

    public getIdString(): string {
        return this.id.toString();
    }

    public getResourceId(): number {
        return this.resourceId;
    }

    public getResourceIdWithId(): string {
        return this.resourceId + '-' + this.id;
    }

    public getFilename(): string {
        return this.filename;
    }

    public getFilesize(): number {
        return this.filesize;
    }

    public getFileSizeInMegaByte(): number {
        const sizeInMB = this.filesize / (1024 * 1024);
        return parseFloat(sizeInMB.toFixed(2));
    }

    public canPartiallyDownload(): boolean {
        return this.getFileSizeInMegaByte() > 100
            && !this.isPdf();
    }

    public calculateParts(): number {
        return this.getFileSizeInMegaByte() <= 500 ? 2 : 4;
    }

    public getFilesizeCalculateParts(): number {
        return Math.ceil(this.getFilesize() / this.calculateParts());
    }

    public getUrl(): string {
        return this.url;
    }

    public getUrlWithToken(): string {
        return this.url + '/' + this.token;
    }

    public setToken(token: string): void {
        this.token = token;
    }

    public getUuid(): string {
        return this.uuid;
    }

    public getName(): string {
        return this.name;
    }

    public getDocumentName(): string {
        return this.documentName;
    }

    public getDescription(): string {
        return this.description;
    }

    public getMimetype(): string {
        return this.mimetype;
    }

    public getFileId(): number {
        return this.fileId;
    }

    public getMediaUuid(): string {
        return this.mediaUuid;
    }

    public getSessionToken(): string {
        return this.sessionToken;
    }

    public getPlaylistId(): string | null {
        return this.playlistId;
    }

    public getEntityChanged(): any {
        return this.entityChanged;
    }

    public getProgress(): number {
        return this.progress;
    }

    public getFormat(): string {
        return this.format;
    }

    public getEntityBundle(): string {
        return this.entityBundle;
    }

    public getTimelapse(): number | null {
        return this.timelapse;
    }

    public getStatus(): Status {
        return this.status;
    }

    public isPendingStatus(): boolean {
        return Status.PENDING === this.getStatus();
    }

    public isDownloadingStatus(): boolean {
        return Status.DOWNLOADING === this.getStatus();
    }

    public isPausedStatus(): boolean {
        return Status.PAUSED === this.getStatus();
    }

    public isProcessingStatus(): boolean {
        return Status.PROCESSING === this.getStatus();
    }

    public setStatus(status: Status): void {
        this.status = status;
    }

    public getPosition(): number {
        return this.position;
    }

    public setPosition(position: number): void {
        this.position = position;
    }

    public getPartialPosition(): number | null {
        return this.partialPosition;
    }

    public getPartialFilename(): string {
        return `${this.getUuid()}_part_${this.getPartialPosition()}`;
    }

    public isEqual(documentDownload: DocumentDownload): boolean {
        return this.getId() === documentDownload.getId();
    }

    public isPdf(): boolean {
        return this.getMimetype() === 'PDF'
            || this.getFormat() === 'PDF';
    }

    public canPause(): boolean {
        return this.getStatus() === Status.DOWNLOADING;
    }

    public canResume(): boolean {
        return this.getStatus() === Status.PAUSED;
    }

    public canCancel(): boolean {
        return this.isPendingStatus()
            || this.isDownloadingStatus()
            || this.isPausedStatus();
    }

    public export(): any {
        return {
            ...this,
            canPartiallyDownload: this.canPartiallyDownload(),
            calculateParts: this.calculateParts()
        };
    }
}
